<template>
  <div>
    <div v-if="!packet.userDetails" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2 text-muted">Initializing…</p>
    </div>

    <div v-else>
      <!-- FILTERS -->
      <b-card style="background: #f8f9fa; margin-bottom: 15px; border: 1px solid #E5E1DA;">
        <b-card-text style="margin-bottom: 0;">
          <div style="display: flex; flex-wrap: wrap; gap: 15px; align-items: flex-end;">
            <div style="width: 250px;">
              <label class="small text-muted mb-1 font-weight-bold">Date Range</label>
              <date-picker v-model="selectedDateRange" range format="MM-DD-YYYY" valueType="format" :clearable="true" :editable="false" placeholder="All dates" style="width: 100%;" @change="applyFilters"></date-picker>
            </div>

            <div style="width: 200px;" v-if="['admin', 'officemanager'].includes(packet.userDetails.userrole)">
              <label class="small text-muted mb-1 font-weight-bold">Ordering Location (To)</label>
              <b-form-select :options="filterLocationOptions" v-model="selectedOrderingLocation" @change="applyFilters"></b-form-select>
            </div>

            <div style="width: 200px;" v-if="['admin', 'officemanager'].includes(packet.userDetails.userrole)">
              <label class="small text-muted mb-1 font-weight-bold">Selling Location (From)</label>
              <b-form-select :options="filterSellingLocationOptions" v-model="selectedSellingLocation" @change="applyFilters"></b-form-select>
            </div>

            <div style="width: 150px;">
              <label class="small text-muted mb-1 font-weight-bold">Status</label>
              <b-form-select :options="filterStatusOptions" v-model="selectedStatus" @change="applyFilters"></b-form-select>
            </div>

            <div>
              <b-button variant="outline-secondary" @click="resetFilters" style="height: 38px;">
                <b-icon icon="x"></b-icon> Clear
              </b-button>
            </div>
          </div>
        </b-card-text>
      </b-card>

      <!-- LOADING / ERROR / EMPTY -->
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary"></b-spinner>
        <p class="mt-2 text-muted">Loading store orders…</p>
      </div>
      <div v-else-if="fetchError" class="vs-card" style="border-color: #F3C9C2; background: #FBE9E7;">
        <b-icon icon="exclamation-triangle-fill" style="color: #C93C30;"></b-icon>
        <strong>Could not load orders:</strong> {{ fetchError }}
        <b-button size="sm" class="vs-btn-ghost ml-3" @click="fetchOrders">Retry</b-button>
      </div>
      <div v-else-if="displayOrders.length === 0" class="vs-card text-center" style="padding: 60px;">
        <b-icon icon="inbox" font-scale="3" style="color: #C9C2B8;"></b-icon>
        <h4 class="mt-3">No store orders found.</h4>
        <p class="text-muted">Try adjusting your filters or create a new order.</p>
      </div>

      <!-- DATA TABLE -->
      <div v-else class="vs-card" style="padding: 0; overflow: hidden;">
        <div class="table-responsive">
          <table class="vs-mini-table" style="min-width: 1630px;">
            <thead style="background: #221F1C;">
              <tr>
                <th style="color: #fff;">Order #</th>
                <th style="color: #fff;">From (Source)</th>
                <th style="color: #fff;">To (Store)</th>
                <th style="color: #fff;">Order Date</th>
                <th style="color: #fff;">Status</th>
                <th style="color: #fff;">Schedule Date</th>
                <th style="color: #fff;">Request Date</th>
                <th style="color: #fff;">Receiving Dates</th>
                <th style="color: #fff; width: 90px;" class="text-right">Total</th>
                <th style="color: #fff; width: 90px;" class="text-right">Total Paid</th>
           
                <th style="color: #fff;" class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in displayOrders" :key="order.id" :style="{ background: order.approved ? '#FFFDF0' : 'transparent' }">
                <td><span class="vs-chip is-orange">{{ getPrefixedOrderId(order) }}</span></td>
                <td>
                  <span v-if="isWarehouse(order.selling_location)" class="fw-bold">MECCA WHOLESALE</span>
                  <template v-else>{{ getLocationName(order.selling_location) }}</template>
                </td>
                <td><strong>{{ getLocationName(order.ordering_location) }}</strong></td>
                <td>{{ formatDate(order.ts) }}</td>
                
                <!-- ============ STATUS (EDITABLE + VALIDATED) ============ -->
                <td style="min-width: 150px;">
                  <b-form-select
                    :options="getStatusOptions(order)"
                    v-model="order.status"
                    style="font-size: 13px;"
                    :disabled="isStatusDisabled(order)"
                    @change="onStatusChange(order)"
                  ></b-form-select>
                  <div v-if="!hasValidDeliveryDate(order)" class="text-danger mt-1" style="font-size: 11px;">
                    <!-- <b-icon icon="exclamation-circle"></b-icon> Request date required -->
                  </div>
                </td>

                <!-- ============ SCHEDULE DATE (EDITABLE) ============ -->
                <td>
                  <date-picker
                    v-model="order.schedule_date"
                    format="MM-DD-YYYY"
                    valueType="format"
                    :disabled="isScheduleDisabled(order)"
                    :clearable="false"
                    :editable="false"
                    placeholder="—"
                    style="width: 130px;"
                    @input="onScheduleChange(order)"
                  ></date-picker>
                </td>

                <!-- ============ REQUEST DATE / delivery_date (EDITABLE) ============ -->
                <td>
                  <date-picker
                    v-model="order.delivery_date"
                    format="MM-DD-YYYY"
                    valueType="format"
                    :disabled="isDeliveryDisabled(order)"
                    :clearable="true"
                    :editable="false"
                    placeholder="—"
                    style="width: 130px;"
                    @input="onDeliveryChange(order)"
                  ></date-picker>
                </td>

                <!-- ============ RECEIVING DATES (expandable) ============ -->
                <td>
                  <div v-if="getReceivingDates(order).length === 0">
                    <span class="text-muted">—</span>
                  </div>
                  <div v-else-if="order.showReceivingDates">
                    <b-button size="sm" variant="link" class="p-0 mb-1" @click="order.showReceivingDates = false">
                      HIDE
                    </b-button>
                    <div v-for="(date, idx) in getReceivingDates(order)" :key="idx" class="vs-date-box">
                      {{ date }}
                    </div>
                  </div>
                  <div v-else>
                    <b-button size="sm" variant="outline-secondary" @click="order.showReceivingDates = true">
                      SHOW {{ getReceivingDates(order).length }} DATE{{ getReceivingDates(order).length > 1 ? 'S' : '' }}
                    </b-button>
                  </div>
                </td>

                <!-- Pricing -->
                <td class="text-right"><strong>${{ formatCurrency(order.amount) }}</strong></td>
                <td class="text-right">
                  <strong class="text-success">${{ formatCurrency(order.paid) }}</strong>
                </td>


                <!-- ============ ACTIONS ============ -->
                <td class="text-center" style="min-width: 260px;">
                  <span v-if="order.loading" class="d-inline-flex align-items-center">
                    <b-spinner small variant="primary"></b-spinner>
                  </span>
                  <span v-else class="d-inline-flex flex-wrap justify-content-center" style="gap: 4px;">
                    <b-button size="sm" class="vs-btn-ghost" @click="$emit('view-order', order)" v-b-tooltip.hover title="View">
                      <b-icon icon="eye"></b-icon>
                    </b-button>

                    <b-button
                      size="sm"
                      variant="outline-success"
                      @click="save_transfer_order(order)"
                      v-b-tooltip.hover
                      title="Save"
                      :disabled="isSaveDisabled(order)"
                    >
                      <b-icon icon="check-square"></b-icon>
                    </b-button>

                    <b-button
                      size="sm"
                      @click="approve_transfer_order(order)"
                      :variant="order.approved ? 'success' : 'outline-secondary'"
                      v-if="['admin', 'officemanager'].includes(packet.userDetails.userrole)"
                      :disabled="!hasValidDeliveryDate(order)"
                      v-b-tooltip.hover
                      :title="order.approved ? 'Approved' : 'Approve'"
                    >
                      <b-icon icon="shield-check"></b-icon>
                    </b-button>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Requestor from '@/utilities/Requestor'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

export default {
  name: "ListStoreOrders",
  components: { DatePicker },
  data() {
    return {
      orders: [],
      loading: false,
      fetchError: null,
      packet: {
        userDetails: { userrole: '' },
        locations: []
      },
      selectedDateRange: null,
      selectedOrderingLocation: null,
      selectedSellingLocation: null,
      selectedStatus: null,
      // All statuses used for editing dropdown (same set as reference transfer-orders)
      allStatuses: [
        { value: 1, text: 'ORDERED' },
        { value: 2, text: 'SCHEDULED' },
        { value: 4, text: 'PARTIAL' },
        { value: 3, text: 'RECEIVED' },
        { value: 5, text: 'COMPLETED' },
        { value: 6, text: 'ARCHIVED' },
      ],
    }
  },
  computed: {
    filterLocationOptions() {
      const locs = this.packet.locations || [];
      const stores = locs.filter(l => !['0', '40', '41', '42', '47', '46'].includes(String(l.value)));
      return [
        { value: null, text: 'All Stores' },
        ...stores.map(l => ({ value: l.value, text: l.text }))
      ];
    },
    filterSellingLocationOptions() {
      const locs = this.packet.locations || [];
      const sources = locs.filter(l =>
        ['0', '40', '41', '42'].includes(String(l.value)) ||
        (!l.text.toLowerCase().includes('online') && !l.text.toLowerCase().includes('out of stock') && String(l.value) !== '47' && String(l.value) !== '46')
      );
      return [
        { value: null, text: 'All Sources' },
        { value: '0', text: 'MECCA WHOLESALE' },
        ...sources.filter(l => String(l.value) !== '0').map(l => ({ value: l.value, text: l.text }))
      ];
    },
    filterStatusOptions() {
      return [
        { value: null, text: 'All Statuses' },
        ...this.allStatuses
      ];
    },
  displayOrders() {
  const HIDDEN_BY_DEFAULT = [3, 5, 6]; // RECEIVED, COMPLETED, ARCHIVED

  return this.orders.filter(order => {
    if (this.selectedDateRange && this.selectedDateRange.length === 2 && this.selectedDateRange[0]) {
      const start = moment(this.selectedDateRange[0], 'MM-DD-YYYY').startOf('day');
      const end = moment(this.selectedDateRange[1], 'MM-DD-YYYY').endOf('day');
      const orderDate = moment(order.ts, 'MM-DD-YYYY HH:mm:ss A');
      if (!orderDate.isBetween(start, end, null, '[]')) return false;
    }
    if (this.selectedOrderingLocation && String(order.ordering_location) !== String(this.selectedOrderingLocation)) {
      return false;
    }
    if (this.selectedSellingLocation) {
      if (String(this.selectedSellingLocation) === '0') {
        if (!['0', '40', '41', '42'].includes(String(order.selling_location))) return false;
      } else {
        if (String(order.selling_location) !== String(this.selectedSellingLocation)) return false;
      }
    }

    // ── STATUS FILTER ──
    // If the user picked a specific status in the dropdown, honor it exactly.
    if (this.selectedStatus) {
      if (parseInt(order.status) !== parseInt(this.selectedStatus)) return false;
    } else {
      // No explicit status chosen → hide RECEIVED / COMPLETED / ARCHIVED by default
      if (HIDDEN_BY_DEFAULT.includes(parseInt(order.status))) return false;
    }

    return true;
  });
}
  },
  mounted() {
    try {
      const rawPacket = JSON.parse(localStorage.packet || '{}');
      const userDetails = JSON.parse(localStorage.userDetails || '{}');
      this.packet = { ...rawPacket, userDetails };
    } catch (e) {
      console.error("Failed to parse localStorage", e);
      this.packet = { userDetails: { userrole: '' }, locations: [] };
    }
    this.fetchOrders();
    this.$root.$on('refresh-store-orders', this.fetchOrders);
  },
  beforeDestroy() {
    this.$root.$off('refresh-store-orders', this.fetchOrders);
  },
  methods: {
    /* ============================================================
     *  DATA LOADING
     * ============================================================ */
    async fetchOrders() {
      this.loading = true;
      this.fetchError = null;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.getAsyncP("/api/sales/get_store_transfer_orders/", {}, this, resolve, reject);
        });
        if (res && res.data) {
          if (res.data.error) {
            this.fetchError = res.data.error;
            this.orders = [];
          } else {
            const result = Array.isArray(res.data.result) ? res.data.result : [];
            this.orders = result
              .filter(o => {
                const orderingIsWh = this.isWarehouse(o.ordering_location);
                const sellingIsWh = this.isWarehouse(o.selling_location);
                return !(orderingIsWh && sellingIsWh);
              })
              .map(o => ({
                ...o,
                // Preserve original_status so validations match transfer-orders exactly
                original_status: o.status,
                // UI toggle state
                showReceivingDates: false,
                // Guard against undefined
                items: Array.isArray(o.items) ? o.items : [],
                loading: false,
              }));
          }
        }
      } catch (e) {
        console.error("fetchOrders error:", e);
        this.fetchError = "Network error. Please try again.";
        this.orders = [];
      } finally {
        this.loading = false;
      }
    },

    /* ============================================================
     *  FILTERS
     * ============================================================ */
    resetFilters() {
      this.selectedDateRange = null;
      this.selectedOrderingLocation = null;
      this.selectedSellingLocation = null;
      this.selectedStatus = null;
    },
    applyFilters() {
      // Computed property does the filtering
    },

    /* ============================================================
     *  HELPERS
     * ============================================================ */
    isWarehouse(locId) {
      return ['0', '40', '41', '42'].includes(String(locId));
    },
    isAdmin() {
      return this.packet.userDetails.userrole === 'admin';
    },
    getPrefixedOrderId(order) {
      if (!order || !order.id) return 'NEW';
      return `STO-${order.id}`;
    },
    getLocationName(value) {
      if (!value) return '—';
      if (String(value) === '0') return 'MECCA WHOLESALE';
      const locs = this.packet.locations || [];
      const found = locs.find(l => String(l.value) === String(value));
      return found ? found.text : value;
    },
    getStatusText(status) {
      const map = {1:'ORDERED', 2:'SCHEDULED', 3:'RECEIVED', 4:'PARTIAL', 5:'COMPLETED', 6:'ARCHIVED'};
      return map[status] || 'UNKNOWN';
    },
    getStatusTagClass(status) {
      const map = {1:'is-warn', 2:'is-teal', 3:'is-teal', 4:'is-warn', 5:'is-teal', 6:''};
      return map[status] || '';
    },
    formatDate(d) {
      return d ? d : "—";
    },
    formatCurrency(amount) {
      return (parseFloat(amount) || 0).toFixed(2);
    },
    formatSimpleDate(value) {
      if (!value) return '—';
      const parsed = moment(value, ['MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true);
      return parsed.isValid() ? parsed.format('MM-DD-YYYY') : value;
    },
    getReceivingDates(order) {
      if (!order || !order.items || !Array.isArray(order.items)) return [];
      return [...new Set(
        order.items
          .filter(i => i.received_date && i.received_date !== 'null' && i.received_date !== '')
          .map(i => i.received_date)
      )];
    },
    getTotalOrdered(order) {
      if (!order.items || !Array.isArray(order.items)) return 0;
      return order.items.reduce((sum, i) => sum + (parseInt(i.quantity) || 0), 0);
    },

    /* ============================================================
     *  VALIDATION — mirror of transfer-orders reference
     * ============================================================ */

    /** Whether order has at least one ordered line item (used by schedule_date guard). */
    hasOrderedItems(order) {
      if (!order.items || !Array.isArray(order.items)) return false;
      return order.items.some(i => (parseInt(i.quantity) || 0) > 0);
    },

    /** Whether delivery_date (Request Date) is a valid non-empty value. */
    hasValidDeliveryDate(order) {
      const d = order && order.delivery_date;
      if (!d) return false;
      if (typeof d === 'string') {
        const t = d.trim();
        if (t === '' || t === 'Invalid date') return false;
      }
      return true;
    },

    /** Status disabled if original was RECEIVED (3) / PARTIAL (4); non-admin locked on 5/6 too. */
    shouldDisableStatusChange(order) {
      if ([5, 6].includes(order.original_status)) return true;
      if (!this.isAdmin() && [5, 6].includes(order.original_status)) return true;
      return false;
    },

    /** Row-level save disabled (same as reference: non-admin locked once 3/4/5). */
    shouldDisableRow(order) {
      if (!order) return true;
      if (!this.isAdmin() && [5, 6].includes(order.status)) return true;
      return false;
    },

    /** Combined "save" disabled = row disabled OR request date missing. */
    isSaveDisabled(order) {
      if (this.shouldDisableRow(order)) return true;
      if (!this.hasValidDeliveryDate(order)) return true;
      return false;
    },

    /** Schedule date editable only if: not received/partial/completed/archived AND has ordered items AND admin. */
    isScheduleDisabled(order) {
      if ([3, 5, 6].includes(order.original_status)) return true;
      if (!this.hasOrderedItems(order)) return true;
      // Schedule date is editable by admin / officemanager only (same as reference)
      return !['admin', 'officemanager'].includes(this.packet.userDetails.userrole);
    },

    /** Request Date editable only by admin / officemanager, and locked once received/partial/completed/archived. */
    isDeliveryDisabled(order) {
      if ([3, 4, 5, 6].includes(order.original_status)) return true;
      return !['admin', 'officemanager'].includes(this.packet.userDetails.userrole);
    },

    /** Status disabled entirely? */
    isStatusDisabled(order) {
      return this.shouldDisableStatusChange(order);
    },

    /**
     * Status dropdown options with per-option validation flags,
     * copied verbatim from transfer-orders reference.
     */
    getStatusOptions(order) {
      return this.allStatuses.map(status => {
        let disabled = false;

        // Locked past REQUESTED/RECEIVED
        if ([5, 6].includes(order.original_status)) {
          disabled = true;
        }

        // Never allow going *into* RECEIVED / PARTIAL manually
        if (['RECEIVED', 'PARTIAL'].includes(status.text)) {
          disabled = true;
        }

        // If currently RECEIVED/PARTIAL, don't allow ARCHIVED
        const currentStatus = this.allStatuses.find(s => s.value === order.status);
        const currentStatusText = currentStatus ? currentStatus.text : null;
        if (['RECEIVED', 'PARTIAL'].includes(currentStatusText) && status.text === 'ARCHIVED') {
          disabled = true;
        }

        // Once RECEIVED/PARTIAL, cannot revert to ORDERED / SCHEDULED
        if ([3, 4].includes(order.original_status) &&
            ['ORDERED', 'SCHEDULED'].includes(status.text)) {
          disabled = true;
        }

        return { ...status, disabled };
      });
    },

    /* ============================================================
     *  CHANGE HANDLERS — mirror reference behaviour
     * ============================================================ */

    /** When Request Date is set on an ORDERED status, auto-bump to SCHEDULED. */
    onDeliveryChange(order) {
      if (order.delivery_date && order.status === 1) {
        order.status = 2;
      }
    },

    /** When Schedule Date is set, clearable is off; just guard against cleared values. */
    onScheduleChange(order) {
      if (!order.schedule_date) return;
      // Auto-bump ordered -> scheduled when schedule date is supplied
      if (order.status === 1) {
        order.status = 2;
      }
    },

    /** Status change guard: block illegal reverts and immediately save. */
    onStatusChange(order) {
      if ([3, 4].includes(order.original_status) && [1, 2].includes(order.status)) {
        this.$bvToast.toast(
          "Cannot revert order status once received, partial, or discrepancy",
          { title: "Error", autoHideDelay: 2000, variant: "warning" }
        );
        order.status = order.original_status;
        return;
      }
    },

    /* ============================================================
     *  API — SAVE / APPROVE (same endpoints as transfer-orders)
     * ============================================================ */

    save_transfer_order(order) {
      // Guard: request date required
      if (!this.hasValidDeliveryDate(order)) {
        this.$bvToast.toast(
          "Request Date is required before saving.",
          { title: "Validation", autoHideDelay: 2000, variant: "warning" }
        );
        return;
      }

      // Guard: illegal status revert
      if ([3, 4].includes(order.original_status) && [1, 2].includes(order.status)) {
        this.$bvToast.toast(
          "Cannot revert order status once received or partial.",
          { title: "Error", autoHideDelay: 2000, variant: "warning" }
        );
        order.status = order.original_status;
        return;
      }

      const data = { ...order };
      const path = order.id
        ? "/api/sales/update_transfer_order/"
        : "/api/sales/record_transfer_order/";

      const that = this;
      const s = function (response) {
        if (response.data.error) {
          that.$bvToast.toast(
            "ERROR SAVING STORE ORDER.",
            { title: "Error", autoHideDelay: 1500, variant: "warning" }
          );
        } else {
          that.$bvToast.toast(
            "STORE ORDER SAVED.",
            { title: "Success", autoHideDelay: 1500, variant: "success" }
          );
          if (Array.isArray(response.data.result) && response.data.result.length > 0) {
            const updated = response.data.result[0];
            if (updated.id) order.id = updated.id;
            if (updated.status !== undefined) {
              order.status = updated.status;
              order.original_status = updated.status;
            }
          }
        }
        order.loading = false;
      };
      const f = function () {
        that.$bvToast.toast(
          "ERROR OCCURRED WITH SAVING STORE ORDER. TRY AGAIN LATER.",
          { title: "Error", autoHideDelay: 1500, variant: "danger" }
        );
        order.loading = false;
      };

      order.loading = true;
      Requestor.postSyncP(path, data, this, s, f);
    },

    approve_transfer_order(order) {
      if (order.approved) return;
      if (!this.hasValidDeliveryDate(order)) {
        this.$bvToast.toast(
          "Request Date is required before approving.",
          { title: "Validation", autoHideDelay: 2000, variant: "warning" }
        );
        return;
      }

      const path = "/api/sales/approve_transfer_order/";
      const data = { id: order.id, approved: true };
      const that = this;
      const s = function (response) {
        if (response.data.error) {
          that.$bvToast.toast(
            "ERROR APPROVING STORE ORDER.",
            { title: "Error", autoHideDelay: 1500, variant: "warning" }
          );
        } else {
          that.$bvToast.toast(
            "SUCCESS APPROVING STORE ORDER.",
            { title: "Success", autoHideDelay: 1500, variant: "success" }
          );
          if (Array.isArray(response.data.result) && response.data.result.length > 0) {
            order.approved = response.data.result[0].approved;
          }
        }
        order.loading = false;
      };
      const f = function () {
        that.$bvToast.toast(
          "ERROR OCCURRED WITH APPROVING STORE ORDER. TRY AGAIN LATER.",
          { title: "Error", autoHideDelay: 1500, variant: "danger" }
        );
        order.loading = false;
      };

      order.loading = true;
      Requestor.postSyncP(path, data, this, s, f);
    },
  }
}
</script>

<style scoped>
.vs-mini-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.vs-mini-table thead th {
  padding: 12px 14px; text-align: left; font-size: 11px;
  text-transform: uppercase; letter-spacing: 0.05em; font-weight: 800;
  border-bottom: 2px solid #E5E1DA; white-space: nowrap;
}
.vs-mini-table tbody td {
  padding: 14px; border-bottom: 1px solid #F1EEE9; vertical-align: middle;
}
.vs-mini-table tbody tr:hover { background: #FAF9F7; }
.vs-chip {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 12px; font-weight: 700; padding: 3px 10px;
  border-radius: 999px; background: #F1EEE9; color: #4A4540;
}
.vs-chip.is-warn   { background: #FFF4DC; color: #8A5E09; }
.vs-chip.is-teal   { background: #E2F2F0; color: #0F6E6B; }
.vs-chip.is-orange { background: #FFF1E4; color: #D4620F; font-family: 'Sora', monospace; }
.vs-card { background: #fff; border: 1px solid #E5E1DA; border-radius: 18px; padding: 20px 22px; }
.vs-btn-ghost.btn {
  border-radius: 10px !important; font-weight: 700; font-size: 13px;
  padding: 5px 12px; display: inline-flex; align-items: center; gap: 6px;
  box-shadow: none !important; background: #fff !important; color: #221F1C !important;
  border: 1.5px solid #E5E1DA !important;
}
.vs-btn-ghost.btn:hover:not(:disabled) { border-color: #EE7A22 !important; color: #D4620F !important; }
.text-danger { color: #C93C30 !important; }
.text-success { color: #0F6E6B !important; }
.fw-bold { font-weight: 700; }

.vs-date-box {
  display: block;
  margin: 4px 0;
  padding: 3px 6px;
  background: #E2F2F0;
  color: #0F6E6B;
  border: 1px solid #C9E1DE;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
}

.mx-datepicker { width: 100%; }
.mx-input {
  height: 34px !important;
  font-size: 13px !important;
  border-radius: 8px !important;
  border: 1.5px solid #E5E1DA !important;
  text-align: center;
}
.mx-input:disabled { background: #F8F8F8 !important; color: #888 !important; }
</style>


// WEBPACK FOOTER //
// src/components/TransferOrders/ListStoreOrders.vue